/**
 * Mobile Calculator Component
 * Extracted from CreateTransactionMobile.tsx
 * 
 * Calculator keyboard for amount and rate input with expression evaluation
 */

import { useState, useEffect, useRef } from "react";
import { Box, Button, Paper } from "@mui/material";
import BackspaceIcon from "@mui/icons-material/Backspace";

export interface MobileCalculatorProps {
  open: boolean;
  initialValue: string;
  onValueChange: (value: string) => void;
  onCheck: (value: string) => void;
  onClose: () => void;
}

/**
 * Evaluate expression with proper operator precedence (PEMDAS/BODMAS)
 */
const evaluateExpression = (expression: string): number => {
  // Remove any trailing operators
  expression = expression.replace(/[+\-*/]$/, "");

  // Replace commas with dots for decimal separator
  expression = expression.replace(/,/g, ".");

  if (!expression || expression === "") {
    return 0;
  }

  // Tokenize the expression - handle negative numbers properly
  const tokens: (number | string)[] = [];
  let currentNumber = "";
  let i = 0;

  // Handle negative at start
  if (expression[0] === "-") {
    currentNumber = "-";
    i = 1;
  }

  while (i < expression.length) {
    const char = expression[i];

    if ((char >= "0" && char <= "9") || char === ".") {
      currentNumber += char;
    } else if (["+", "-", "*", "/"].includes(char)) {
      // Push the number if we have one
      if (currentNumber !== "" && currentNumber !== "-") {
        tokens.push(parseFloat(currentNumber));
        currentNumber = "";
      }

      // Handle consecutive operators (like +- or --)
      if (char === "-" && tokens.length > 0 && typeof tokens[tokens.length - 1] === "string") {
        // Previous token is an operator, treat - as negative sign
        currentNumber = "-";
      } else {
        // Normal operator
        tokens.push(char);
      }
    }
    i++;
  }

  // Push the last number if we have one
  if (currentNumber !== "" && currentNumber !== "-") {
    tokens.push(parseFloat(currentNumber));
  }

  if (tokens.length === 0) {
    return 0;
  }

  // First number must be a number, not an operator
  if (tokens.length > 0 && typeof tokens[0] === "string") {
    return 0;
  }

  // Evaluate multiplication and division first (left to right)
  const processedTokens: (number | string)[] = [];
  i = 0;

  while (i < tokens.length) {
    if (typeof tokens[i] === "string" && (tokens[i] === "*" || tokens[i] === "/")) {
      // This is a * or / operator
      const prev = processedTokens.pop() as number;
      const operator = tokens[i] as string;
      i++;
      if (i < tokens.length && typeof tokens[i] === "number") {
        const next = tokens[i] as number;

        if (operator === "*") {
          processedTokens.push(prev * next);
        } else {
          processedTokens.push(next !== 0 ? prev / next : 0);
        }
        i++;
      } else {
        // Invalid expression
        return 0;
      }
    } else {
      processedTokens.push(tokens[i]);
      i++;
    }
  }

  // Evaluate addition and subtraction (left to right)
  let result = processedTokens[0] as number;
  for (let i = 1; i < processedTokens.length; i += 2) {
    if (i + 1 < processedTokens.length) {
      const operator = processedTokens[i] as string;
      const next = processedTokens[i + 1] as number;

      if (operator === "+") {
        result += next;
      } else if (operator === "-") {
        result -= next;
      }
    }
  }

  return result;
};

export default function MobileCalculator({
  open,
  initialValue,
  onValueChange,
  onCheck,
  onClose,
}: MobileCalculatorProps) {
  const [calculatorExpression, setCalculatorExpression] = useState<string>(initialValue || "0");
  const prevOpenRef = useRef<boolean>(false);
  const prevInitialValueRef = useRef<string>("__INIT__");
  const onValueChangeRef = useRef(onValueChange);
  const hasUserTypedRef = useRef<boolean>(false);
  const prevExpressionRef = useRef<string>(initialValue || "0");
  const isUserTypingRef = useRef<boolean>(false); // Track if user is actively typing
  const lastButtonProcessedRef = useRef<{ btn: string; at: number } | null>(null);
  const backspaceLongPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backspaceLongPressHandledRef = useRef<boolean>(false);
  const backspacePointerDownRef = useRef<boolean>(false);
  const LONG_PRESS_MS = 800; // Clear-all only after holding; single tap must delete one character
  
  // Keep ref updated
  useEffect(() => {
    onValueChangeRef.current = onValueChange;
  }, [onValueChange]);

  // Clear backspace long-press timer on unmount
  useEffect(() => {
    return () => {
      if (backspaceLongPressTimerRef.current) {
        clearTimeout(backspaceLongPressTimerRef.current);
      }
    };
  }, []);
  
  // Sync calculator state to hook state whenever it changes (but not during initial open)
  // This ensures the display always shows what the user is typing
  useEffect(() => {
    // Only sync if expression changed and user has typed (to avoid syncing during initial open)
    // Also check that calculator is open to avoid syncing when closed
    // Sync immediately when user types - this ensures display updates in real-time
    if (open && hasUserTypedRef.current) {
      const currentExpr = calculatorExpression;
      const prevExpr = prevExpressionRef.current;
      
      // Only sync if expression actually changed
      if (currentExpr !== prevExpr) {
        // Update prevExpressionRef immediately to prevent duplicate syncs and race conditions
        prevExpressionRef.current = currentExpr;
        // Sync to hook state - use requestAnimationFrame to ensure it happens after render
        // but before the next frame, ensuring smooth updates
        requestAnimationFrame(() => {
          // Double-check that user is still typing or expression hasn't been reset
          if (calculatorExpression === currentExpr) {
            onValueChangeRef.current(currentExpr);
          }
        });
      }
    } else if (!hasUserTypedRef.current && open) {
      // Update prevExpressionRef when calculator opens (even if we're not syncing)
      prevExpressionRef.current = calculatorExpression;
    }
  }, [calculatorExpression, open]);
  
  // Update calculator expression when calculator opens or initialValue changes
  // Only reset on open, or when initialValue changes while open (if user hasn't typed)
  useEffect(() => {
    const wasClosed = !prevOpenRef.current;
    const isNowOpen = open;
    const currentInitialValue = initialValue || "0";
    
    // Always reset when calculator opens (transitions from closed to open)
    // This ensures clean state when user clicks on amount field again
    if (isNowOpen && wasClosed) {
      // Update calculator's internal state to match initialValue
      // Use functional update to ensure we're working with the latest state
      setCalculatorExpression(() => currentInitialValue);
      prevInitialValueRef.current = currentInitialValue;
      prevExpressionRef.current = currentInitialValue; // Initialize prevExpressionRef to prevent false syncs
      hasUserTypedRef.current = false; // Reset flag when calculator opens
      isUserTypingRef.current = false; // Reset typing flag when calculator opens
      
      // Note: Hook state is already set by parent before calculator opens,
      // so we don't need to call onValueChange here to avoid race conditions
      // The parent sets hook state via setCalculatorExpression(rawValue) before opening
    }
    
    // When user just backspaced: parent may have updated (e.g. flushSync) before child state committed,
    // so child can still show old value. Sync child to parent when initialValue is a prefix of expression (user backspaced).
    if (hasUserTypedRef.current && currentInitialValue.length >= 0 && calculatorExpression.length > currentInitialValue.length && calculatorExpression.startsWith(currentInitialValue)) {
      setCalculatorExpression(() => currentInitialValue);
      prevInitialValueRef.current = currentInitialValue;
      prevExpressionRef.current = currentInitialValue;
      prevOpenRef.current = open;
      return;
    }

    // Check if initialValue changed while calculator is open (e.g., from refresh button)
    // If the new initialValue is completely different from current expression, it's likely a refresh
    // In this case, reset the state flags so backspace works correctly
    const hasOperators = /[+\-*/]/.test(calculatorExpression);
    const initialValueHasOperators = /[+\-*/]/.test(currentInitialValue);
    const isExpressionLonger = calculatorExpression.length > currentInitialValue.length;
    const expressionStartsWithInitial = calculatorExpression.startsWith(currentInitialValue) || currentInitialValue.startsWith(calculatorExpression);
    
    if (isNowOpen && !wasClosed && currentInitialValue !== prevInitialValueRef.current) {
      // If initialValue changed and current expression doesn't match it, it's likely a refresh
      // Reset flags so backspace works correctly after refresh
      const isRefreshUpdate = calculatorExpression !== currentInitialValue && 
                             !hasOperators && 
                             !initialValueHasOperators &&
                             !(isExpressionLonger && expressionStartsWithInitial);
      
      if (isRefreshUpdate) {
        setCalculatorExpression(() => currentInitialValue);
        prevInitialValueRef.current = currentInitialValue;
        prevExpressionRef.current = currentInitialValue;
        hasUserTypedRef.current = false; // Reset flag so backspace works correctly
        isUserTypingRef.current = false; // Reset typing flag
        prevOpenRef.current = open;
        return;
      }
    }

    // NEVER reset if user is actively typing - this prevents the disappearing issue
    // Also NEVER reset if current expression contains operators (user is building an expression like "32-5")
    
    // If current expression has operators, NEVER reset (user is building an expression)
    // Also NEVER reset if expression is longer (user is typing more digits)
    // Also NEVER reset if user has typed (prevents reset when user presses backspace)
    // But still update prevInitialValueRef to prevent false resets later
    if (hasOperators || (isExpressionLonger && expressionStartsWithInitial && calculatorExpression !== "0") || hasUserTypedRef.current) {
      // Don't reset - user is actively building an expression, typing more digits, or has already typed
      // Update prevInitialValueRef to match current initialValue to prevent false resets
      if (currentInitialValue !== prevInitialValueRef.current) {
        prevInitialValueRef.current = currentInitialValue;
      }
      prevOpenRef.current = open;
      return;
    }
    
    // Only reset if initialValue changed and user is NOT actively typing
    // IMPORTANT: Don't reset if user has typed (hasUserTypedRef.current) - this prevents resetting when user presses backspace
    if (isNowOpen && !wasClosed && !isUserTypingRef.current && !hasUserTypedRef.current && !initialValueHasOperators && currentInitialValue !== prevInitialValueRef.current) {
      // Only reset if initialValue changed, user is NOT actively typing, user hasn't typed yet, AND neither expression has operators
      // This handles the case when user clicks amount field again while calculator is open
      // But prevents resetting when user is building an expression (e.g., "32-5")
      setCalculatorExpression(() => currentInitialValue);
      prevInitialValueRef.current = currentInitialValue;
      prevExpressionRef.current = currentInitialValue;
    }
    
    prevOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialValue]); // Include initialValue to detect changes when re-entering amount field

  // Handle calculator button clicks - supports chained operations
  const handleCalculatorButton = (btn: string) => {
    // Ignore duplicate events (e.g. click + pointer on mobile) within 80ms
    const now = Date.now();
    const last = lastButtonProcessedRef.current;
    if (last && last.btn === btn && now - last.at < 80) {
      return;
    }
    lastButtonProcessedRef.current = { btn, at: now };
    
    // Mark that user has typed (before state update)
    hasUserTypedRef.current = true;
    isUserTypingRef.current = true; // Mark that user is actively typing
    
    // Calculate new expression based on current state and button pressed
    setCalculatorExpression((currentExpression) => {
      let expression = currentExpression;

      if (btn === "C") {
        return "0";
      } else if (btn === "⌫") {
        if (expression.length <= 1) {
          expression = "0";
        } else {
          expression = expression.slice(0, -1);
          if (expression === "" || ["+", "-", "*", "/"].includes(expression)) {
            expression = "0";
          }
        }
        return expression;
      } else if (btn === "." || btn === ",") {
        // Decimal point - add to current number (support both . and ,)
        const lastChar = expression[expression.length - 1];
        const lastNumber = expression.split(/[+\-*/]/).pop() || "";

        // Only add decimal if current number doesn't have one
        if (!lastNumber.includes(".") && !lastNumber.includes(",")) {
          if (["+", "-", "*", "/"].includes(lastChar) || expression === "0") {
            expression = expression === "0" ? "0." : expression + "0.";
          } else {
            expression = expression + ".";
          }
        }
        // Don't call onValueChangeRef here - let the useEffect handle it
      } else if (["+", "-", "*", "/", "x", "÷"].includes(btn)) {
        // Operator - append to expression (support x for * and ÷ for /)
        const lastChar = expression[expression.length - 1];
        const operator = btn === "x" ? "*" : btn === "÷" ? "/" : btn;

        // When value is "0" (or empty) and user taps "-", show "-" immediately (negative amount), not "0-"
        if ((expression === "0" || expression === "") && operator === "-") {
          expression = "-";
        } else if (["+", "-", "*", "/"].includes(lastChar)) {
          // If last character is an operator, replace it
          expression = expression.slice(0, -1) + operator;
        } else {
          // Append operator to expression
          expression = expression + operator;
        }
        // Don't call onValueChangeRef here - let the useEffect handle it
      } else if (btn === "=") {
        // Equals - evaluate expression (handled by the button onClick, not here)
        return expression;
      } else if (btn === "00") {
        // Double zero
        if (expression === "0") {
          expression = "0";
        } else {
          expression = expression + "00";
        }
        // Don't call onValueChangeRef here - let the useEffect handle it
      } else if (btn === "000") {
        // Triple zero
        if (expression === "0") {
          expression = "0";
        } else {
          expression = expression + "000";
        }
        // Don't call onValueChangeRef here - let the useEffect handle it
      } else {
        // Number
        if (expression === "0") {
          expression = btn;
        } else {
          expression = expression + btn;
        }
        // Don't call onValueChangeRef here - let the useEffect handle it to avoid double updates
      }

      // Don't call onValueChangeRef here - let the useEffect handle it to avoid double updates
      // The useEffect will sync the state change to the parent
      return expression;
    });
    // Note: The useEffect hook will sync the state change to the parent
    // Keep typing flag true - it will only be reset when calculator closes or user explicitly stops
    // This prevents any resets from happening while the user is actively using the calculator
    // The flag will be reset when the calculator closes (in the useEffect that handles opening)
  };

  if (!open) return null;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: "calc(20px + env(safe-area-inset-bottom))",
          width: "100%",
          overflow: "visible",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          boxSizing: "border-box",
          zIndex: 1300,
          border: "none",
          borderTop: "2px solid",
          borderColor: "primary.main",
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
          boxShadow: (theme) => theme.palette.mode === "dark" ? "0 -4px 20px rgba(0, 0, 0, 0.4)" : "0 -4px 20px rgba(0, 0, 0, 0.15), 0 -2px 8px rgba(0, 0, 0, 0.1)",
          minHeight: "380px",
          pointerEvents: "auto",
          WebkitTransform: "translate3d(0, 0, 0)",
          transform: "translate3d(0, 0, 0)",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", p: 2, width: "100%" }}>
          {/* Calculator buttons */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, width: "100%" }}>
            {/* Row 1: +, -, x, ÷ */}
            {["+", "-", "x", "÷"].map((btn) => (
              <Button
                key={btn}
                variant="outlined"
                onClick={() => handleCalculatorButton(btn)}
                sx={{
                  py: 2,
                  fontSize: "1.2rem",
                  minWidth: 0,
                  borderColor: "divider",
                  color: "text.primary",
                  "&:hover": {
                    borderColor: "text.secondary",
                    bgcolor: "action.hover",
                  },
                }}
              >
                {btn}
              </Button>
            ))}

            {/* Row 2: 7, 8, 9, = */}
            {["7", "8", "9"].map((btn) => (
              <Button
                key={btn}
                variant="outlined"
                onClick={() => handleCalculatorButton(btn)}
                sx={{
                  py: 2,
                  fontSize: "1.2rem",
                  minWidth: 0,
                  borderColor: "divider",
                  color: "text.primary",
                  "&:hover": {
                    borderColor: "text.secondary",
                    bgcolor: "action.hover",
                  },
                }}
              >
                {btn}
              </Button>
            ))}
            <Button
              variant="outlined"
              onClick={() => {
                // Calculate expression and show result (don't close calculator)
                const result = evaluateExpression(calculatorExpression);
                const finalValue = result.toString();
                // Mark that user has typed so useEffect will sync
                hasUserTypedRef.current = true;
                isUserTypingRef.current = true;
                setCalculatorExpression(finalValue);
                // Note: useEffect will sync the state change to parent
                // Keep typing flag true - it will only be reset when calculator closes
                // This prevents any resets from happening while the user is actively using the calculator
              }}
              sx={{
                py: 2,
                fontSize: "1.2rem",
                minWidth: 0,
                borderColor: "divider",
                color: "text.primary",
                "&:hover": {
                  borderColor: "text.secondary",
                  bgcolor: "action.hover",
                },
              }}
            >
              =
            </Button>

            {/* Row 3: 4, 5, 6, , */}
            {["4", "5", "6"].map((btn) => (
              <Button
                key={btn}
                variant="outlined"
                onClick={() => handleCalculatorButton(btn)}
                sx={{
                  py: 2,
                  fontSize: "1.2rem",
                  minWidth: 0,
                  borderColor: "divider",
                  color: "text.primary",
                  "&:hover": {
                    borderColor: "text.secondary",
                    bgcolor: "action.hover",
                  },
                }}
              >
                {btn}
              </Button>
            ))}
            <Button
              variant="outlined"
              onClick={() => handleCalculatorButton(",")}
              sx={{
                py: 2,
                fontSize: "1.2rem",
                minWidth: 0,
                borderColor: "divider",
                color: "text.primary",
                "&:hover": {
                  borderColor: "text.secondary",
                  bgcolor: "action.hover",
                },
              }}
            >
              ,
            </Button>

            {/* Row 4: 1, 2, 3, ⌫ */}
            {["1", "2", "3"].map((btn) => (
              <Button
                key={btn}
                variant="outlined"
                onClick={() => handleCalculatorButton(btn)}
                sx={{
                  py: 2,
                  fontSize: "1.2rem",
                  minWidth: 0,
                  borderColor: "divider",
                  color: "text.primary",
                  "&:hover": {
                    borderColor: "text.secondary",
                    bgcolor: "action.hover",
                  },
                }}
              >
                {btn}
              </Button>
            ))}
            <Button
              variant="outlined"
              onPointerDown={() => {
                backspaceLongPressHandledRef.current = false;
                backspacePointerDownRef.current = true;
                backspaceLongPressTimerRef.current = setTimeout(() => {
                  backspaceLongPressTimerRef.current = null;
                  if (!backspacePointerDownRef.current) return;
                  backspaceLongPressHandledRef.current = true;
                  handleCalculatorButton("C");
                }, LONG_PRESS_MS);
              }}
              onPointerUp={() => {
                const wasLongPress = backspaceLongPressHandledRef.current;
                backspacePointerDownRef.current = false;
                if (backspaceLongPressTimerRef.current) {
                  clearTimeout(backspaceLongPressTimerRef.current);
                  backspaceLongPressTimerRef.current = null;
                }
                if (!wasLongPress) handleCalculatorButton("⌫");
              }}
              onPointerLeave={() => {
                backspacePointerDownRef.current = false;
                if (backspaceLongPressTimerRef.current) {
                  clearTimeout(backspaceLongPressTimerRef.current);
                  backspaceLongPressTimerRef.current = null;
                }
              }}
              onPointerCancel={() => {
                backspacePointerDownRef.current = false;
                if (backspaceLongPressTimerRef.current) {
                  clearTimeout(backspaceLongPressTimerRef.current);
                  backspaceLongPressTimerRef.current = null;
                }
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (backspaceLongPressHandledRef.current) backspaceLongPressHandledRef.current = false;
              }}
              sx={{
                py: 2,
                fontSize: "1.2rem",
                minWidth: 0,
                borderColor: "divider",
                color: "text.primary",
                "&:hover": {
                  borderColor: "text.secondary",
                  bgcolor: "action.hover",
                },
              }}
            >
              <BackspaceIcon sx={{ fontSize: "1.25rem" }} />
            </Button>

            {/* Row 5: 00, 0, 000, Check */}
            <Button
              variant="outlined"
              onClick={() => handleCalculatorButton("00")}
              sx={{
                py: 2,
                fontSize: "1.2rem",
                minWidth: 0,
                borderColor: "divider",
                color: "text.primary",
                "&:hover": {
                  borderColor: "text.secondary",
                  bgcolor: "action.hover",
                },
              }}
            >
              00
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleCalculatorButton("0")}
              sx={{
                py: 2,
                fontSize: "1.2rem",
                minWidth: 0,
                borderColor: "divider",
                color: "text.primary",
                "&:hover": {
                  borderColor: "text.secondary",
                  bgcolor: "action.hover",
                },
              }}
            >
              0
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleCalculatorButton("000")}
              sx={{
                py: 2,
                fontSize: "1.2rem",
                minWidth: 0,
                borderColor: "divider",
                color: "text.primary",
                "&:hover": {
                  borderColor: "text.secondary",
                  bgcolor: "action.hover",
                },
              }}
            >
              000
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                // Evaluate expression if it contains operators, otherwise use as-is
                let finalValue = calculatorExpression.replace(/[+\-*/]$/, "");

                // Check if expression contains an operator (equation)
                if (/[+\-*/]/.test(finalValue)) {
                  try {
                    // Evaluate the expression
                    const result = evaluateExpression(finalValue);
                    finalValue = result.toString();
                  } catch (error) {
                    // If evaluation fails, use the expression as-is
                    console.error("Error evaluating expression:", error);
                  }
                }

                // Don't convert "0" to empty string here - let the handler decide based on field type
                // This allows amount fields to keep "0" while rate fields can convert it to empty
                onCheck(finalValue);
              }}
              sx={{
                py: 2,
                fontSize: "1.2rem",
                minWidth: 0,
              }}
            >
              ✓
            </Button>
          </Box>
        </Box>
      </Paper>
      {/* Bottom offset area with same background color */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          height: "calc(20px + env(safe-area-inset-bottom))",
          bgcolor: "background.paper",
          zIndex: 1299,
          borderTop: "1px solid",
          borderColor: "background.paper",
        }}
      />
    </>
  );
}
